import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createMessagingConversations, getMessages, getMessagesUpdates, getMessagingConversations, markMessagesAsRead, sendMessage } from '@uplift/api';
import * as batshit from '@yornaath/batshit';
import { BaseQueryOpts } from '../baseTypes';
import { useCurrentuser } from '@uplift/contexts';


export const useUserMessagesUpdatesQuery = (opts?: BaseQueryOpts) => {
  const me = useCurrentuser();

  return useQuery({
    queryKey: [ 'messaging', 'updates' ],
    queryFn: () => getMessagesUpdates(),
    select: (response) => response.data,
    refetchInterval: 10000,
    enabled: !!me?.id,
    ...opts,
  });
};


const markMessageAsResolved = batshit.create({
  fetcher: async (messageIds: string[]) => {
    await markMessagesAsRead(messageIds.filter(Boolean));

    return null;
  },
  resolver: () => null,
  scheduler: batshit.windowScheduler(500)
});


export const useMarkMessagesAsReadMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: { messageId: string }) => markMessageAsResolved.fetch(args.messageId),
    onSuccess: async () => {
      await queryClient.invalidateQueries([ 'messaging' ]);
    }
  });
};


export const useUserMessagesQuery = (conversationId: string, opts?: BaseQueryOpts) => {
  const { data: key } = useUserMessagesUpdatesQuery();

  return useQuery({
    queryKey: [ 'messaging', conversationId, 'messages', key ],
    keepPreviousData: true,
    queryFn: () => getMessages(conversationId),
    select: (response) => response.data,
    refetchInterval: 5000,
    ...opts,
  });
};


export const useSendUserMessageMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: {
      conversationId: string;
      message: string;
      attachmentIds?: string[];
    }) => sendMessage(args.conversationId, args.message, args.attachmentIds),
    onSuccess: async (_response) => {
      await queryClient.invalidateQueries([ 'messaging', 'updates' ]);
    }
  });
};


export const useUserConversationsQuery = (opts?: BaseQueryOpts) => {
  const { data: key } = useUserMessagesUpdatesQuery();

  return useQuery({
    queryKey: [ 'messaging', 'conversations', key ],
    keepPreviousData: true,
    queryFn: () => getMessagingConversations(),
    select: (response) => response.data,
    ...opts,
  });
};


export const useCreateUserConversationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: { participantId: string }) => createMessagingConversations(args.participantId),
    onSuccess: async () => {
      await queryClient.invalidateQueries([ 'messaging', 'updates' ]);
      await queryClient.invalidateQueries([ 'messaging', 'conversations' ]);
    }
  });
};
